/**
 * Status page component for PowerEditor - handles non-running workspace states
 */

import { Link } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  Server,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getWorkspaceStatusColor } from "@/lib/utils/status-utils";
import { PowerEditorStatusPageProps } from "@/types/power-editor.types";

export default function PowerEditorStatusPage({
  project,
  workspace,
  isResuming,
  loading,
  error,
  onStartWorkspace,
  projectId,
}: PowerEditorStatusPageProps): JSX.Element {
  // Show error state when there's an error and no workspace
  if (!workspace && error) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <div className="space-y-6">
            <div className="relative">
              <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-lg font-medium text-foreground">
                {error.toLowerCase().includes("not found") ? "Project not found" : "Error loading project"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {error}
              </p>
            </div>
            <Link to="/dashboard/editor">
              <Button className="mt-2">Back to Projects</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state when no workspace and no error
  if (!workspace) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-500" />
          <p className="text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b px-4 h-14 flex items-center justify-between bg-card border-border">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-foreground">{project?.name}</span>
        </div>
        <Badge className={getWorkspaceStatusColor(workspace.status)}>
          {workspace.status}
        </Badge>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          {workspace.status === "provisioning" && (
            <div className="space-y-6">
              <div className="relative">
                <div className="w-16 h-16 mx-auto rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-medium text-foreground">
                  Setting up your workspace
                </h2>
                <p className="text-sm text-muted-foreground">
                  This may take 2-3 minutes
                </p>
              </div>
            </div>
          )}

          {workspace.status === "starting" && (
            <div className="space-y-6">
              <div className="relative">
                <div className="w-16 h-16 mx-auto rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-medium text-foreground">
                  Starting workspace
                </h2>
                <p className="text-sm text-muted-foreground">
                  Almost ready...
                </p>
              </div>
            </div>
          )}

          {workspace.status === "paused" && isResuming && (
            <div className="space-y-6">
              <div className="relative">
                <div className="w-16 h-16 mx-auto rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-medium text-foreground">
                  Resuming workspace
                </h2>
                <p className="text-sm text-muted-foreground">
                  This may take 30-60 seconds
                </p>
              </div>
            </div>
          )}

          {workspace.status === "stopped" && (
            <div className="space-y-6">
              <div className="relative">
                <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
                  <Server className="w-8 h-8 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-medium text-foreground">
                  Workspace stopped
                </h2>
                <p className="text-sm text-muted-foreground">
                  Start the workspace to continue
                </p>
              </div>
              <Button
                onClick={onStartWorkspace}
                disabled={loading}
                className="mt-2">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  "Start Workspace"
                )}
              </Button>
            </div>
          )}

          {workspace.status === "error" && (
            <div className="space-y-6">
              <div className="relative">
                <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-destructive" />
                </div>
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-medium text-foreground">
                  Workspace error
                </h2>
                <p className="text-sm text-muted-foreground">
                  Something went wrong. Please check workspace settings.
                </p>
              </div>
              <Link to="/dashboard/settings/workspaces">
                <Button className="mt-2">View Workspaces</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}